import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { to: "/", label: "Home", testId: "site-nav-link-home" },
  { to: "/services", label: "Services", testId: "site-nav-link-services" },
  { to: "/about", label: "About", testId: "site-nav-link-about" },
  { to: "/contact", label: "Contact", testId: "site-nav-link-contact" },
];

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
          scrolled
            ? "bg-[rgba(11,10,8,0.86)] backdrop-blur-md border-b border-[color:var(--hairline)]"
            : "bg-transparent"
        }`}
      >
        <div className="container-page flex items-center justify-between h-[72px] md:h-[84px]">
          <Link
            to="/"
            data-testid="site-nav-logo"
            className="flex items-center gap-3 group"
            aria-label="Bishop Creative home"
          >
            <span
              aria-hidden
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: "var(--brass)" }}
            />
            <span
              className="font-display text-[22px] md:text-[24px] tracking-tight"
              style={{ color: "var(--text-1)" }}
            >
              Bishop Creative
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `font-mono text-[11px] tracking-[0.24em] uppercase transition-colors duration-200 ${
                    isActive
                      ? "text-[color:var(--text-1)]"
                      : "text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link
              to="/contact"
              data-testid="site-nav-cta"
              className="btn btn-ghost"
              style={{ height: 40, padding: "0 18px" }}
            >
              Start a Project
            </Link>
          </div>

          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            data-testid="site-nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-[color:var(--hairline-strong)] text-[color:var(--text-1)]"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-30 md:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <div
          className="absolute inset-0"
          style={{ background: "rgba(11,10,8,0.96)" }}
          onClick={() => setOpen(false)}
        />
        <div className="relative container-page pt-28 pb-16 flex flex-col gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`${l.testId}-mobile`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `font-display text-4xl leading-none tracking-tight transition-colors duration-200 ${
                  isActive
                    ? "text-[color:var(--text-1)]"
                    : "text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-8">
            <div className="hairline" />
            <div className="mt-6 eyebrow">San Luis Obispo, California</div>
            <div className="mt-2 text-sm text-[color:var(--text-3)]">
              hello@bishopcreative.co
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
